import type { Unsubscribe } from '../types/events';

/**
 * Type-safe event map constraint - allows any object with string keys mapping to functions
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EventMap = { [K in string]?: (...args: any[]) => void };

/**
 * Extract event names from an event map
 */
export type EventName<T> = Extract<keyof T, string>;

/**
 * Extract handler type for a specific event
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EventHandler<T, K extends EventName<T>> = T[K] extends (...args: any[]) => void ? T[K] : never;

/**
 * Listener entry with metadata
 */
interface ListenerEntry<T, K extends EventName<T>> {
  handler: EventHandler<T, K>;
  once: boolean;
}

/**
 * Type-safe EventEmitter implementation
 *
 * @example
 * ```typescript
 * interface MyEvents {
 *   'data': (data: string) => void;
 *   'error': (error: Error) => void;
 *   'count': (n: number) => void;
 * }
 *
 * const emitter = new EventEmitter<MyEvents>();
 * emitter.on('data', (data) => console.log(data)); // data is typed as string
 * emitter.emit('data', 'hello'); // Only string allowed
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class EventEmitter<T = Record<string, (...args: any[]) => void>> {
  /** Map of event names to listener arrays */
  private listeners = new Map<string, ListenerEntry<T, EventName<T>>[]>();

  /** Maximum listeners per event (0 = unlimited) */
  private maxListeners = 10;

  /**
   * Subscribe to an event
   *
   * @param event - Event name
   * @param handler - Event handler function
   * @returns Unsubscribe function
   */
  on<K extends EventName<T>>(event: K, handler: EventHandler<T, K>): Unsubscribe {
    return this.addListener(event, handler, false);
  }

  /**
   * Subscribe to an event (once)
   * Handler will be automatically removed after first invocation
   *
   * @param event - Event name
   * @param handler - Event handler function
   * @returns Unsubscribe function
   */
  once<K extends EventName<T>>(event: K, handler: EventHandler<T, K>): Unsubscribe {
    return this.addListener(event, handler, true);
  }

  /**
   * Unsubscribe from an event
   *
   * @param event - Event name
   * @param handler - Event handler function to remove
   */
  off<K extends EventName<T>>(event: K, handler: EventHandler<T, K>): void {
    const eventListeners = this.listeners.get(event);
    if (!eventListeners) return;

    const index = eventListeners.findIndex((entry) => entry.handler === handler);
    if (index !== -1) {
      eventListeners.splice(index, 1);
      if (eventListeners.length === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Emit an event to all subscribers
   *
   * @param event - Event name
   * @param args - Event arguments
   * @returns Whether any handlers were called
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emit<K extends EventName<T>>(event: K, ...args: any[]): boolean {
    const eventListeners = this.listeners.get(event as string);
    if (!eventListeners || eventListeners.length === 0) {
      return false;
    }

    // Copy array to avoid issues if handlers modify listeners
    const listeners = [...eventListeners];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toRemove: ListenerEntry<T, any>[] = [];

    for (const entry of listeners) {
      try {
        (entry.handler as (...args: unknown[]) => void)(...args);
        if (entry.once) {
          toRemove.push(entry);
        }
      } catch (error) {
        // Emit error event if available, otherwise rethrow
        if (event !== 'error' && this.listeners.has('error' as string)) {
          this.emit('error' as K, error);
        } else {
          console.error(`Error in event handler for '${event}':`, error);
        }
      }
    }

    // Remove once listeners
    for (const entry of toRemove) {
      this.off(event, entry.handler);
    }

    return true;
  }

  /**
   * Remove all listeners for an event or all events
   *
   * @param event - Optional event name. If omitted, removes all listeners.
   */
  removeAllListeners<K extends EventName<T>>(event?: K): void {
    if (event !== undefined) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Get the number of listeners for an event
   *
   * @param event - Event name
   * @returns Number of listeners
   */
  listenerCount<K extends EventName<T>>(event: K): number {
    const eventListeners = this.listeners.get(event);
    return eventListeners?.length ?? 0;
  }

  /**
   * Get all event names with registered listeners
   *
   * @returns Array of event names
   */
  eventNames(): EventName<T>[] {
    return Array.from(this.listeners.keys()) as EventName<T>[];
  }

  /**
   * Get all listeners for an event
   *
   * @param event - Event name
   * @returns Array of handler functions
   */
  getListeners<K extends EventName<T>>(event: K): EventHandler<T, K>[] {
    const eventListeners = this.listeners.get(event);
    return (eventListeners?.map((entry) => entry.handler) ?? []) as EventHandler<T, K>[];
  }

  /**
   * Get the maximum number of listeners per event
   *
   * @returns Maximum listeners
   */
  getMaxListeners(): number {
    return this.maxListeners;
  }

  /**
   * Set the maximum number of listeners per event
   *
   * @param n - Maximum listeners (0 = unlimited)
   */
  setMaxListeners(n: number): void {
    this.maxListeners = n;
  }

  /**
   * Add a listener with the specified options
   *
   * @param event - Event name
   * @param handler - Event handler function
   * @param once - Whether to remove after first invocation
   * @returns Unsubscribe function
   */
  private addListener<K extends EventName<T>>(
    event: K,
    handler: EventHandler<T, K>,
    once: boolean
  ): Unsubscribe {
    let eventListeners = this.listeners.get(event);
    if (!eventListeners) {
      eventListeners = [];
      this.listeners.set(event, eventListeners);
    }

    // Warn if exceeding max listeners
    if (this.maxListeners > 0 && eventListeners.length >= this.maxListeners) {
      console.warn(
        `MaxListenersExceededWarning: Possible memory leak detected. ` +
        `${eventListeners.length + 1} '${event}' listeners added. ` +
        `Use emitter.setMaxListeners() to increase limit.`
      );
    }

    const entry: ListenerEntry<T, K> = { handler, once };
    eventListeners.push(entry as ListenerEntry<T, EventName<T>>);

    return () => this.off(event, handler);
  }
}

/**
 * Create a new typed EventEmitter
 */
export function createEventEmitter<T extends EventMap>(): EventEmitter<T> {
  return new EventEmitter<T>();
}
